import { MapPin, Phone, MessageCircle, Navigation, Languages, Car } from "lucide-react";
import { BUSINESS, waLink } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import { GoldDivider } from "@/components/GoldDivider";
import { useLang } from "@/i18n";

export const LocationSection = () => {
    const { t } = useLang();
    const HOURS = [
        { day: t("location.monFri"), time: "10:00 AM a 6:00 PM" },
        { day: t("location.sat"), time: "10:00 AM a 2:00 PM" },
        { day: t("location.sun"), time: t("location.closed") },
    ];

    return (
        <section id="ubicacion" data-testid="location-section" className="relative py-24 sm:py-32 px-5 sm:px-8 bg-ink-deep">
            <div className="max-w-6xl mx-auto">
                <Reveal className="text-center mb-14">
                    <p className="font-accent text-[11px] tracking-[0.4em] uppercase text-gold-soft mb-4">
                        {t("location.eyebrow")}
                    </p>
                    <h2 className="font-display font-semibold gold-text text-4xl sm:text-5xl tracking-wide">
                        {t("location.title")}
                    </h2>
                    <GoldDivider className="mt-8" />
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <Reveal>
                        <div className="h-full bg-surface border border-gold/20 rounded-sm p-8 sm:p-10 flex flex-col">
                            <h3 className="font-display text-2xl font-semibold text-cream tracking-wide">
                                {BUSINESS.name}
                            </h3>
                            <p className="flex items-start gap-2.5 text-sm text-cream/70 font-light mt-4">
                                <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                                <span data-testid="location-address">
                                    4124 N 19th Ave<br />Phoenix, AZ 85015
                                </span>
                            </p>
                            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                                <span className="inline-flex items-center gap-2 text-xs text-gold-soft" data-testid="location-languages">
                                    <Languages className="w-3.5 h-3.5" /> {t("location.languages")}
                                </span>
                                <span className="inline-flex items-center gap-2 text-xs text-gold-soft" data-testid="location-parking">
                                    <Car className="w-3.5 h-3.5" /> {t("location.parking")}
                                </span>
                            </div>

                            <div className="mt-8 flex-1">
                                <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-gold-soft mb-4">
                                    {t("location.hours")}
                                </p>
                                <dl className="divide-y divide-gold/10" data-testid="location-hours">
                                    {HOURS.map((h) => (
                                        <div key={h.day} className="flex items-center justify-between py-3">
                                            <dt className="text-sm text-cream/75 font-light">{h.day}</dt>
                                            <dd className={`text-sm tracking-wide ${h.time === t("location.closed") ? "bg-burgundy/30 px-2 py-0.5 rounded-sm text-cream/70" : "text-gold-soft"}`}>
                                                {h.time}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>

                            <p className="flex items-center gap-2.5 text-sm text-cream/70 font-light mt-8">
                                <Phone className="w-4 h-4 text-gold shrink-0" />
                                {BUSINESS.phoneDisplay}
                            </p>

                            <div className="grid grid-cols-2 gap-3 mt-6">
                                <a href={`tel:${BUSINESS.phoneTel}`} data-testid="location-call-button"
                                    className="inline-flex items-center justify-center gap-2 border border-gold/50 text-gold font-accent text-[10px] tracking-[0.18em] uppercase px-4 py-3 rounded-sm hover:bg-gold/10 transition-colors duration-300">
                                    <Phone className="w-3.5 h-3.5" /> {t("location.call")}
                                </a>
                                <a href={waLink(t("wa.appointment"))} target="_blank" rel="noopener noreferrer" data-testid="location-whatsapp-button"
                                    className="inline-flex items-center justify-center gap-2 border border-wa/70 text-[#7fd6c9] font-accent text-[10px] tracking-[0.18em] uppercase px-4 py-3 rounded-sm hover:bg-wa/15 transition-colors duration-300">
                                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                </a>
                            </div>
                            <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BUSINESS.mapsQuery)}`}
                                target="_blank" rel="noopener noreferrer" data-testid="location-directions-button"
                                className="mt-3 inline-flex items-center justify-center gap-2 bg-gold text-ink font-accent text-[10px] tracking-[0.18em] uppercase font-bold px-4 py-3 rounded-sm hover:brightness-110 hover:shadow-[0_0_22px_rgba(212,175,55,0.35)] transition-[box-shadow,filter] duration-300"
                            >
                                <Navigation className="w-3.5 h-3.5" /> {t("location.directions")}
                            </a>
                            <p className="text-[11px] text-sand font-light text-center mt-4 leading-relaxed">
                                {t("location.caption")}
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12}>
                        <div className="h-full min-h-[24rem] border border-gold/20 rounded-sm overflow-hidden ornament-frame">
                            <iframe
                                title={t("location.mapTitle")}
                                data-testid="location-map"
                                src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.mapsQuery)}&output=embed`}
                                className="w-full h-full min-h-[24rem] map-dark"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                            />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
